// Quoting analytics dashboard (Session AAV, Loop 1). One aggregate read
// (GET /api/quotes/analytics) rendered as calm, dark panels per the design direction:
// conversion funnel (Sent -> Opened -> Accepted -> Signed with drop-through), win and
// loss with reasons, the ARR pipeline (live vs awaiting signature vs signed), value
// stats, tier and lift-type distribution, and the monthly trend. Every segment links
// to the quotes list pre-filtered to the underlying quotes (QuotesPage reads the URL
// params). The date range selects the quote cohort by creation date.
import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { QuoteAnalytics } from "../../lib/api";
import { monthLabel, PIPELINE_STAGES_CLIENT, PIPELINE_STAGE_LABELS_CLIENT } from "../../lib/quoteFunnel";
import { SkeletonPanel } from "../../components/Skeleton";
import { PageHeader } from "../../components/PageHeader";
import { ButtonLink } from "../../components/Button";

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

// A whole date (yyyy-mm-dd) n days before today, for the preset ranges.
function daysAgoIso(days: number): string {
  const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

// Percentage of a base, guarded for a zero base. Shown as a whole number.
function pct(part: number, base: number): string {
  if (base <= 0) return "0%";
  return `${Math.round((part / base) * 100)}%`;
}

// The default (no bounds sent) matches the server's bounded default window, so the label is
// honest about what the office is looking at.
const PRESETS: { label: string; days: number | null }[] = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Last 12 months", days: 365 },
  { label: "Last 24 months", days: null },
];

// A quiet elevated panel (the design direction's card: slightly lighter than the page,
// never a loud fill).
function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="font-heading text-lg tracking-wide text-ele-strong">{title}</h2>
      {subtitle && <p className="mt-0.5 text-xs text-ele-body opacity-80">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

// A small coloured dot beside its word (colour never the only signal).
function Dot({ color }: { color: string }) {
  return <span aria-hidden className="inline-block h-2 w-2 flex-none rounded-full" style={{ backgroundColor: color }} />;
}

// A quiet horizontal bar: width proportional to value/max, brand colour, never a loud block.
// The hairline ring keeps a dark fill (the locked Care purple) legible against the dark track.
function Bar({ value, max, color, title }: { value: number; max: number; color: string; title?: string }) {
  const width = max > 0 ? Math.max(value > 0 ? 2 : 0, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-ele-inset-strong" title={title}>
      <div
        className="h-full rounded-full shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)] transition-[width] duration-300"
        style={{ width: `${width}%`, backgroundColor: color }}
      />
    </div>
  );
}

// Brand segment colours (locked palette + functional tokens only; danger is the --ele-danger
// token, never an ad-hoc red). The word always accompanies the colour.
const C = {
  green: "#63C41F",
  blue: "#00C4FF",
  loler: "#2CB9F3",
  purple: "#7d21c7",
  amber: "#F59E0B",
  danger: "#FF5A5A",
  // Session AFK: the neutral series tone follows the body token (#CACACA in dark, unchanged)
  // so a fallback bar or dot stays visible on the light ground too.
  body: "var(--ele-body)",
};

const TIER_SERIES: Record<string, string> = { core: C.green, care: C.purple, complete: C.blue };

export function QuoteAnalyticsPage() {
  const [data, setData] = useState<QuoteAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  // A client-side guard message for a reversed custom range (the server also rejects it).
  const [rangeError, setRangeError] = useState<string | null>(null);

  const load = useCallback(async (range: { from?: string; to?: string }) => {
    setLoading(true);
    setLoadError(false);
    try {
      setData(await api.quotes.analytics(range));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load({});
  }, [load]);

  function applyPreset(days: number | null) {
    const nextFrom = days == null ? "" : daysAgoIso(days);
    setFrom(nextFrom);
    setTo("");
    setRangeError(null);
    void load(nextFrom ? { from: nextFrom } : {});
  }

  function applyCustom() {
    if (from && to && from > to) {
      setRangeError("The end date is before the start date. Swap the dates and try again.");
      return;
    }
    setRangeError(null);
    void load({ ...(from ? { from } : {}), ...(to ? { to } : {}) });
  }

  // Every segment deep-link carries the active range, so the list the office lands on shows
  // the same cohort the clicked figure was computed over. The no-bounds default (last 24
  // months server-side) carries nothing: the list then shows everything, which for ELE's data
  // is the same cohort.
  const withRange = useCallback(
    (path: string) => {
      const params: string[] = [];
      if (from) params.push(`from=${encodeURIComponent(from)}`);
      if (to) params.push(`to=${encodeURIComponent(to)}`);
      if (params.length === 0) return path;
      return `${path}${path.includes("?") ? "&" : "?"}${params.join("&")}`;
    },
    [from, to],
  );

  const funnelRows = data
    ? [
        { key: "sent", label: "Sent", count: data.funnel.sent, color: C.blue, to: withRange("/office/quotes?filter=sent") },
        { key: "opened", label: "Opened", count: data.funnel.opened, color: C.loler, to: withRange("/office/quotes?filter=opened") },
        // Cumulative: a signed quote passed through acceptance, so this links to the
        // reached-acceptance filter (accepted OR signed), matching the count exactly.
        { key: "accepted", label: "Accepted", count: data.funnel.accepted, color: C.amber, to: withRange("/office/quotes?filter=reached_accepted") },
        { key: "signed", label: "Signed", count: data.funnel.signed, color: C.green, to: withRange("/office/quotes?filter=signed") },
      ]
    : [];

  const maxMonthly = data ? Math.max(1, ...data.monthly.map((m) => Math.max(m.priced, m.sent, m.signed))) : 1;

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Quote conversion"
        subtitle="How quotes convert: from sent to signed, what is won and lost, and the value in the pipeline."
        actions={
          <Link
            to="/office/quotes"
            className="shrink-0 rounded-ele border border-ele-purple-accent px-4 py-2.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
          >
            Open quotes list
          </Link>
        }
      />

      {/* Date range: preset chips + custom whole-date bounds. The range selects the quote
          cohort by creation date. */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => {
          const active = p.days == null ? from === "" && to === "" : from === daysAgoIso(p.days) && to === "";
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p.days)}
              aria-pressed={active}
              className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                active ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
              }`}
            >
              {p.label}
            </button>
          );
        })}
        <span className="mx-1 hidden h-5 w-px bg-ele-purple-accent sm:inline-block" aria-hidden />
        <label className="flex items-center gap-1.5 text-xs text-ele-body">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-xs text-ele-strong"
          />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-ele-body">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-xs text-ele-strong"
          />
        </label>
        <button
          type="button"
          onClick={applyCustom}
          className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong"
        >
          Apply
        </button>
      </div>
      {rangeError && (
        <p role="alert" className="mb-4 text-xs text-ele-danger">
          {rangeError}
        </p>
      )}

      {loading ? (
        <div role="status" aria-live="polite" className="grid gap-4 lg:grid-cols-2">
          <span className="sr-only">Loading the quote analytics...</span>
          <SkeletonPanel />
          <SkeletonPanel />
          <SkeletonPanel />
          <SkeletonPanel />
        </div>
      ) : loadError ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">Could not load the quote analytics.</p>
          <button
            type="button"
            onClick={() => void load({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong"
          >
            Retry
          </button>
        </div>
      ) : !data || data.totals.quotes === 0 ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-8 text-center">
          <p className="font-heading text-lg text-ele-strong">No quotes in this range</p>
          <p className="mt-1 text-sm text-ele-body">Widen the date range, or price a new quote to start the funnel.</p>
          <ButtonLink to="/office/quotes/new" className="mt-4" icon={<Plus aria-hidden size={15} />}>
            New quote
          </ButtonLink>
        </div>
      ) : (
        <>
          {/* Headline stats */}
          <div className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <p className="font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">Quotes priced</p>
              <p className="mt-1 font-heading text-2xl text-ele-strong">{data.totals.quotes}</p>
              <p className="text-xs text-ele-body opacity-80">{data.totals.sent} sent to customers</p>
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <p className="font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">Average quote value</p>
              <p className="mt-1 font-heading text-2xl text-ele-strong">
                {data.totals.averageAnnual != null ? `${gbp(data.totals.averageAnnual)}/yr` : "None yet"}
              </p>
              <p className="text-xs text-ele-body opacity-80">ex VAT, across the range</p>
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <p className="font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">Median time to accept</p>
              <p className="mt-1 font-heading text-2xl text-ele-strong">
                {data.totals.medianDaysToAccept != null ? `${data.totals.medianDaysToAccept} days` : "None yet"}
              </p>
              <p className="text-xs text-ele-body opacity-80">from send to customer acceptance</p>
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <p className="font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">Signed in range</p>
              <p className="mt-1 font-heading text-2xl" style={{ color: C.green }}>
                {gbp(data.pipeline.signed.annual)}/yr
              </p>
              <p className="text-xs text-ele-body opacity-80">
                {data.pipeline.signed.count} {data.pipeline.signed.count === 1 ? "contract" : "contracts"} signed
              </p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Conversion funnel */}
            <Panel
              title="Conversion funnel"
              subtitle="Bar lengths compare each stage with the quotes sent; each percentage compares a stage with the one before it. Click a stage to see those quotes."
            >
              <ul className="space-y-3">
                {funnelRows.map((row, i) => {
                  const prev = i === 0 ? row.count : funnelRows[i - 1]!.count;
                  return (
                    <li key={row.key}>
                      <Link to={row.to} className="group block">
                        <div className="mb-1 flex items-baseline justify-between gap-2">
                          <span className="flex items-center gap-2 text-sm text-ele-body group-hover:text-ele-strong">
                            <Dot color={row.color} />
                            {row.label}
                          </span>
                          <span className="text-xs text-ele-body">
                            <span className="font-heading text-base text-ele-strong">{row.count}</span>
                            {i > 0 && <span className="ml-2 opacity-70">{pct(row.count, prev)} of {funnelRows[i - 1]!.label.toLowerCase()}</span>}
                          </span>
                        </div>
                        <Bar value={row.count} max={Math.max(1, data.funnel.sent)} color={row.color} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-ele-body opacity-70">
                Overall: {pct(data.funnel.signed, data.funnel.sent)} of sent quotes signed.
              </p>
            </Panel>

            {/* ARR pipeline */}
            <Panel title="ARR pipeline" subtitle="Annual contract value by stage: live with the customer, out for signature, and signed.">
              <ul className="space-y-3">
                {[
                  { key: "live", label: "Awaiting customer", seg: data.pipeline.live, color: C.blue, to: withRange("/office/quotes?filter=live") },
                  { key: "awaiting", label: "Awaiting signature", seg: data.pipeline.awaitingSignature, color: C.amber, to: withRange("/office/quotes?filter=accepted") },
                  { key: "signed", label: "Signed", seg: data.pipeline.signed, color: C.green, to: withRange("/office/quotes?filter=signed") },
                ].map((row) => {
                  const maxAnnual = Math.max(1, data.pipeline.live.annual, data.pipeline.awaitingSignature.annual, data.pipeline.signed.annual);
                  return (
                    <li key={row.key}>
                      <Link to={row.to} className="group block">
                        <div className="mb-1 flex items-baseline justify-between gap-2">
                          <span className="flex items-center gap-2 text-sm text-ele-body group-hover:text-ele-strong">
                            <Dot color={row.color} />
                            {row.label}
                          </span>
                          <span className="text-xs text-ele-body">
                            <span className="font-heading text-base text-ele-strong">{gbp(row.seg.annual)}/yr</span>
                            <span className="ml-2 opacity-70">{row.seg.count} {row.seg.count === 1 ? "quote" : "quotes"}</span>
                          </span>
                        </div>
                        <Bar value={row.seg.annual} max={maxAnnual} color={row.color} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Panel>

            {/* Session ASH (Q-9): project quotations created in the same range, their OWN segment, staged by the
                same mapping as the pipeline strip on the Quotes list. Kept apart from the maintenance funnel above
                so its conversion figures keep their meaning. Absent from a server not yet redeployed. */}
            {data.projects && (
              <Panel
                title="Project quotations"
                subtitle="Project quotations raised in this range, by stage. One-off values, kept apart from the maintenance figures."
              >
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {PIPELINE_STAGES_CLIENT.map((stage) => (
                    <div key={stage}>
                      <dt className="text-xs text-ele-body opacity-80">{PIPELINE_STAGE_LABELS_CLIENT[stage]}</dt>
                      <dd className="font-heading text-base text-ele-strong">
                        {data.projects!.stages[stage].count}
                        <span className="ml-2 text-xs text-ele-body opacity-70">{gbp(data.projects!.stages[stage].value)}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-3 text-sm text-ele-body">
                  {data.projects.conversionPct == null
                    ? "No project quotation in this range has reached the customer yet."
                    : `${data.projects.won} of ${data.projects.sent} sent ${data.projects.sent === 1 ? "quotation was" : "quotations were"} signed (${data.projects.conversionPct}%).`}
                </p>
              </Panel>
            )}

            {/* Win and loss. The Declined tile only renders once a decline has actually been
                recorded: there is no customer decline path yet, so a permanent zero would read
                as a business fact rather than an unbuilt feature. */}
            <Panel title="Win and loss" subtitle="Signed wins against quotes marked lost, with the reasons the office recorded.">
              <div className={`grid gap-3 ${data.winLoss.declined.count > 0 ? "grid-cols-3" : "grid-cols-2"}`}>
                {[
                  { label: "Won", seg: data.winLoss.won, color: C.green, to: withRange("/office/quotes?filter=signed") },
                  { label: "Lost", seg: data.winLoss.lost, color: C.danger, to: withRange("/office/quotes?filter=lost") },
                  ...(data.winLoss.declined.count > 0
                    ? [{ label: "Declined", seg: data.winLoss.declined, color: C.danger, to: withRange("/office/quotes?filter=declined") }]
                    : []),
                ].map((t) => (
                  <Link key={t.label} to={t.to} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3 transition hover:border-ele-blue">
                    <p className="flex items-center gap-2 font-menu text-[0.65rem] uppercase tracking-widest text-ele-body">
                      <Dot color={t.color} />
                      {t.label}
                    </p>
                    <p className="mt-1 font-heading text-xl text-ele-strong">{t.seg.count}</p>
                    <p className="text-xs text-ele-body opacity-80">{gbp(t.seg.annual)}/yr</p>
                  </Link>
                ))}
              </div>
              {data.winLoss.reasons.length > 0 && (
                <div className="mt-4">
                  <p className="mb-2 font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">Loss reasons</p>
                  <ul className="space-y-2">
                    {data.winLoss.reasons.map((r) => (
                      <li key={r.id ?? "none"}>
                        {/* Each reason row filters the list to THAT reason (?reason=id), not just "lost". */}
                        <Link
                          to={withRange(r.id ? `/office/quotes?filter=lost&reason=${encodeURIComponent(r.id)}` : "/office/quotes?filter=lost")}
                          className="group flex items-baseline justify-between gap-2"
                        >
                          <span className="text-sm text-ele-body group-hover:text-ele-strong">{r.label}</span>
                          <span className="text-xs text-ele-body">
                            <span className="font-heading text-sm text-ele-strong">{r.count}</span>
                            <span className="ml-2 opacity-70">{gbp(r.annual)}/yr</span>
                          </span>
                        </Link>
                        <Bar value={r.count} max={Math.max(1, data.winLoss.lost.count)} color={C.danger} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Panel>

            {/* Distributions */}
            <Panel title="Quotes by tier and lift type" subtitle="What is being recommended, and for which equipment. Click a row to see those quotes.">
              <p className="mb-2 font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">By recommended tier</p>
              <ul className="space-y-2">
                {data.byTier.map((t) => (
                  <li key={t.tier}>
                    <Link to={withRange(`/office/quotes?tier=${encodeURIComponent(t.tier)}`)} className="group block">
                      <div className="mb-1 flex items-baseline justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm text-ele-body group-hover:text-ele-strong">
                          <Dot color={TIER_SERIES[t.tier] ?? C.body} />
                          {t.label}
                        </span>
                        <span className="text-xs text-ele-body">
                          <span className="font-heading text-sm text-ele-strong">{t.count}</span>
                          <span className="ml-2 opacity-70">{gbp(t.annual)}/yr</span>
                        </span>
                      </div>
                      <Bar value={t.count} max={Math.max(1, data.totals.quotes)} color={TIER_SERIES[t.tier] ?? C.body} />
                    </Link>
                  </li>
                ))}
              </ul>
              {data.byLiftType.length > 0 && (
                <>
                  <p className="mb-2 mt-4 font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">By lift type</p>
                  <ul className="space-y-2">
                    {data.byLiftType.map((l) => (
                      <li key={l.key}>
                        <Link to={withRange(`/office/quotes?liftType=${encodeURIComponent(l.key)}`)} className="group block">
                          <div className="mb-1 flex items-baseline justify-between gap-2">
                            <span className="truncate text-sm text-ele-body group-hover:text-ele-strong">{l.label}</span>
                            <span className="font-heading text-sm text-ele-strong">{l.count}</span>
                          </div>
                          <Bar value={l.count} max={Math.max(1, data.byLiftType[0]?.count ?? 1)} color={C.blue} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Panel>
          </div>

          {/* Monthly trend */}
          {data.monthly.length > 0 && (
            <div className="mt-4">
              <Panel title="Monthly trend" subtitle="Quotes priced in the calculator, sent to customers, and signed, month by month. Click a month to see its quotes.">
                {/* Session ACW Loop 6: this is a genuinely matrix-shaped BI table, not a
                    navigable list -- Priced/Sent/Signed only mean something read together, and
                    the Activity column is itself a small inline bar chart (two stacked Bar
                    components) that needs real width to compare months, so hiding columns below
                    md would defeat the point rather than declutter it. It already wraps in its own
                    max-w-full overflow-x-auto, so a narrow phone scrolls the TABLE, never the page. */}
                <div data-hscroll className="max-w-full overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left text-sm">
                    <thead className="font-menu text-[0.65rem] uppercase tracking-widest text-ele-blue">
                      <tr>
                        <th className="py-1.5 pr-3">Month</th>
                        <th className="py-1.5 pr-3">Priced</th>
                        <th className="py-1.5 pr-3">Sent</th>
                        <th className="py-1.5 pr-3">Signed</th>
                        <th className="py-1.5 pr-3">Signed value</th>
                        <th className="w-1/3 py-1.5">
                          Activity
                          {/* Legend: the two stacked bars, named so colour is never the only signal. */}
                          <span className="ml-2 inline-flex items-center gap-1 normal-case tracking-normal text-ele-body opacity-80">
                            <Dot color={C.blue} /> Sent
                            <Dot color={C.green} /> Signed
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.monthly.map((m) => (
                        <tr key={m.month} className="border-t border-ele-purple-accent/60">
                          <td className="py-2 pr-3">
                            <Link to={`/office/quotes?month=${encodeURIComponent(m.month)}`} className="text-ele-body underline decoration-dotted hover:text-ele-strong">
                              {monthLabel(m.month)}
                            </Link>
                          </td>
                          <td className="py-2 pr-3 text-ele-body">{m.priced}</td>
                          <td className="py-2 pr-3 text-ele-body">{m.sent}</td>
                          <td className="py-2 pr-3">
                            <span className={m.signed > 0 ? "text-ele-green" : "text-ele-body opacity-70"}>{m.signed}</span>
                          </td>
                          <td className="py-2 pr-3 text-ele-body">{m.signedAnnual > 0 ? `${gbp(m.signedAnnual)}/yr` : ""}</td>
                          <td className="py-2">
                            <div className="space-y-1">
                              <Bar value={m.sent} max={maxMonthly} color={C.blue} title={`Sent: ${m.sent}`} />
                              <Bar value={m.signed} max={maxMonthly} color={C.green} title={`Signed: ${m.signed}`} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          )}
        </>
      )}
    </div>
  );
}
